import { useCallback, useEffect, useState } from 'react';
import { useAccount } from '../account/accountStore.ts';
import type { Result } from '../account/types.ts';
import type { MetaClient } from './types.ts';

/** The progress service of the account that is signed in, once the account service has loaded. */
export function useMetaClient(): MetaClient | null {
  const client = useAccount((s) => s.client);
  return client ? client.meta() : null;
}

export type RemoteStatus = 'loading' | 'ready' | 'error';

export interface Remote<T> {
  status: RemoteStatus;
  data: T | null;
  reload: () => void;
}

/**
 * Runs a request when the screen opens and says where it stands. `load` must be stable (made with
 * `useCallback`): a new one is a new request. `null` means "not yet" (nothing to ask of).
 */
export function useRemote<T>(load: (() => Promise<Result<T>>) | null): Remote<T> {
  const [state, setState] = useState<{ load: unknown; result: Result<T> } | null>(null);
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!load) return;
    let alive = true;
    void load().then((result) => {
      if (alive) setState({ load, result });
    });
    return () => {
      alive = false;
    };
  }, [load, round]);

  const reload = useCallback(() => {
    setState(null);
    setRound((n) => n + 1);
  }, []);

  const current = state && state.load === load ? state.result : null;
  return {
    status: !load || current === null ? 'loading' : current.ok ? 'ready' : 'error',
    data: current?.ok ? current.value : null,
    reload,
  };
}
